// Sharing policy for AI-action generation metadata (prompt, settings, inputs) on
// posted answers. Structured action requests are PRIVATE by default and the
// inline treatment is WYSIWYG: everyone — the answer owner included — sees the
// bare token (model name only) until the owner shares the action publicly
// (metadata_public), which restores the generation card and the info affordance
// for all viewers. The owner's extra ability is the click-through: their tap
// always opens the action-card popover, which carries the share toggle and
// read-only input viewers.
//
// Legacy answers without structured action_requests carry their prompt as
// ordinary visible body text, so their tokens stay open to everyone.
//
// The server mirrors this policy: read payloads for non-owners strip
// prompt-bearing fields and mark stripped hashes with prompt_stripped (see
// Answer.answer_json_with_private_ai_action_metadata), so the client gates here
// are presentation, not the privacy boundary.

export const aiActionMetadataPublic = action => action?.metadata_public === true

export const aiActionPromptStripped = action => action?.prompt_stripped === true

// Inline token treatment (prompt text, generation-card mode, info icon):
// flag-only, identical for every viewer.
export const aiActionInlineMetadataVisible = action => !action || aiActionMetadataPublic(action)

// Whether clicking the token opens the action-card popover for this viewer.
export const aiActionPopoverAvailable = ({ action, isOwner }) =>
    aiActionInlineMetadataVisible(action) || isOwner === true

export const answerOwnedByUser = (answer, userId) =>
    Boolean(answer?.user_id && userId && String(answer.user_id) === String(userId))

export const aiActionWithMetadataPublic = (action, metadataPublic) => {
    if (!action || typeof action !== 'object') return action
    return {
        ...action,
        metadata_public: metadataPublic === true,
    }
}

export const actionRequestsWithMetadataPublicAt = (actionRequests, actionIndex, metadataPublic) => {
    const actions = Array.isArray(actionRequests) ? actionRequests : []
    if (actionIndex < 0 || actionIndex >= actions.length) return actions
    return actions.map((action, index) =>
        index === actionIndex ? aiActionWithMetadataPublic(action, metadataPublic) : action,
    )
}

const actionRequestIdValues = action =>
    [action?.id, action?.client_id, action?.ai_action_id]
        .filter(value => value != null && value !== '')
        .map(value => String(value))

const tokenAttrsIdValue = attrs => {
    const value =
        attrs?.ai_action_id ||
        attrs?.actionId ||
        attrs?.action_id ||
        attrs?.action_request_id ||
        attrs?.id ||
        null
    return value != null && value !== '' ? String(value) : null
}

const TOKEN_ATTR_PROMPT_FIELDS = [
    'lyrics',
    'negative_prompt',
    'negativePrompt',
    'next_text',
    'nextText',
    'pay',
    'previous_text',
    'previousText',
    'promptContent',
    'prompt_content',
    'textInputValue',
]

// Read-only render form of a delta: private action tokens lose their prompt
// attrs so the static body renders the bare token with no post-mount collapse
// (the server already strips non-owner payloads; this covers the owner's own
// view, which keeps the full metadata for editing and the popover). Returns the
// input delta untouched (same reference) when there is nothing to scrub, so
// memoized HTML generation keyed on the value stays stable.
export const scrubPrivateActionPromptsFromDelta = (delta, actionRequests) => {
    const ops = Array.isArray(delta?.ops) ? delta.ops : null
    if (!ops) return delta

    const actions = Array.isArray(actionRequests)
        ? actionRequests
        : actionRequests && typeof actionRequests === 'object'
        ? [actionRequests]
        : []
    if (!actions.some(action => action && typeof action === 'object')) return delta

    const publicActionIds = new Set()
    const publicActionIndexes = new Set()
    actions.forEach((action, index) => {
        if (!action || typeof action !== 'object') return
        if (!aiActionMetadataPublic(action)) return
        actionRequestIdValues(action).forEach(id => publicActionIds.add(id))
        publicActionIndexes.add(index)
    })

    let tokenIndex = -1
    let changed = false
    const scrubbedOps = ops.map(op => {
        const attrs = op?.attributes?.actionTagAttrs
        if (!attrs || typeof attrs !== 'object') return op

        tokenIndex += 1
        const tokenId = tokenAttrsIdValue(attrs)
        const tokenIsPublic = tokenId
            ? publicActionIds.has(tokenId)
            : publicActionIndexes.has(tokenIndex)
        if (tokenIsPublic) return op

        if (!TOKEN_ATTR_PROMPT_FIELDS.some(field => attrs[field] != null)) return op

        changed = true
        const scrubbedAttrs = { ...attrs }
        TOKEN_ATTR_PROMPT_FIELDS.forEach(field => {
            delete scrubbedAttrs[field]
        })
        return {
            ...op,
            attributes: {
                ...op.attributes,
                actionTagAttrs: scrubbedAttrs,
            },
        }
    })

    return changed ? { ...delta, ops: scrubbedOps } : delta
}
